import { useState, useEffect, useCallback, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Stack,
  Slider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import * as pdfjsLib from 'pdfjs-dist';

import { UserContext } from '../../context/UserContext';
import DocumentApi from '../../services/DocumentApi';
import defaultLogo from '../../watermark.svg';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.mjs`;

const DocumentViewer = ({
  open,
  onClose,
  document: doc,
  showWatermark = true,
  viewMode = 'auto', // 'auto', 'original', 'watermarked'
  onStatusChange,
  isProducer = false,
}) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [zoom, setZoom] = useState(100);

  const documentApi = new DocumentApi();

  // Consumer access control based on payment status
  const isConsumerUnpaid = showWatermark;
  const hasWatermark = !!doc?.watermarkedUrl;
  const isBlocked = isConsumerUnpaid && !hasWatermark;

  // Determine which URL to use based on viewMode:
  // - 'original': always show original
  // - 'watermarked': always show watermarked (if exists)
  // - 'auto' (default): use showWatermark prop to determine
  const getDisplayUrl = () => {
    if (viewMode === 'original') {
      return doc?.originalUrl || doc?.url;
    }
    if (viewMode === 'watermarked') {
      return doc?.watermarkedUrl; // No fallback for watermarked mode
    }
    // 'auto' mode - use showWatermark prop
    if (showWatermark) {
      return doc?.watermarkedUrl; // Unpaid: watermarked only, no fallback
    }
    return doc?.originalUrl || doc?.url; // Paid: original
  };

  const displayUrl = getDisplayUrl();

  // Download always uses original (only available when paid or producer)
  const downloadUrl = doc?.originalUrl || doc?.url;

  // Determine file type from stored fileType or fallback to URL extension
  const isPdf = doc?.fileType === 'application/pdf' || displayUrl?.toLowerCase().endsWith('.pdf');
  const isImage = doc?.fileType?.startsWith('image/') || displayUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

  // Dynamic watermarks only needed for producer preview in 'watermarked' mode
  const shouldApplyDynamicWatermark = viewMode === 'watermarked' && !doc?.watermarkedUrl;

  const applyWatermark = useCallback(async (canvas, context, applyDynamic) => {
    // Skip dynamic watermark if not needed (producer view or pre-generated watermark exists)
    if (!applyDynamic) return;

    const logoImg = new Image();
    logoImg.src = defaultLogo;
    await logoImg.decode();

    const tileWidth = canvas.width * 0.15;
    const tileHeight = tileWidth * (logoImg.height / logoImg.width);
    const spacingX = tileWidth * 2;
    const spacingY = tileHeight * 2;

    context.globalAlpha = 0.25;
    for (let y = -tileHeight; y < canvas.height + tileHeight; y += spacingY) {
      for (let x = -tileWidth; x < canvas.width + tileWidth; x += spacingX) {
        context.save();
        context.translate(x + tileWidth / 2, y + tileHeight / 2);
        context.rotate(-Math.PI / 6);
        context.drawImage(logoImg, -tileWidth / 2, -tileHeight / 2, tileWidth, tileHeight);
        context.restore();
      }
    }
    context.globalAlpha = 1;
  }, []);

  const processPdf = useCallback(async (url, applyDynamic) => {
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      const imageList = [];

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 2 });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: context, viewport }).promise;
        await applyWatermark(canvas, context, applyDynamic);

        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        imageList.push(dataUrl);
      }

      setImages(imageList);
    } catch (err) {
      console.error('Error processing PDF:', err);
      throw new Error('Failed to load PDF document');
    }
  }, [applyWatermark]);

  const processImage = useCallback(async (url, applyDynamic) => {
    try {
      const image = new Image();
      image.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = url;
      });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = image.width;
      canvas.height = image.height;
      context.drawImage(image, 0, 0);

      await applyWatermark(canvas, context, applyDynamic);

      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      setImages([dataUrl]);
    } catch (err) {
      console.error('Error processing image:', err);
      throw new Error('Failed to load image');
    }
  }, [applyWatermark]);

  useEffect(() => {
    // Skip loading if blocked (unpaid consumer without watermark)
    if (!open || isBlocked) {
      setLoading(false);
      return;
    }
    if (!displayUrl) return;

    const loadDocument = async () => {
      setLoading(true);
      setError(null);
      setCurrentPage(0);
      setZoom(100);

      try {
        if (isPdf) {
          await processPdf(displayUrl, shouldApplyDynamicWatermark);
        } else if (isImage) {
          await processImage(displayUrl, shouldApplyDynamicWatermark);
        } else {
          // For non-PDF/image files, just show link
          setImages([]);
        }

        // Record view if user is logged in (skip for producers)
        if (user?.uid && doc?.id) {
          await documentApi.recordDocumentView(
            doc.id,
            user.uid,
            { email: user.email || user.user?.email, name: user.name || user.user?.name },
            isProducer
          );
          onStatusChange?.();
        }
      } catch (err) {
        setError(err.message || 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, displayUrl, doc?.id, shouldApplyDynamicWatermark, viewMode, isBlocked]);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 25, 50));
  const handleZoomChange = (_, value) => setZoom(value);

  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 0));
  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, images.length - 1));

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  // Only allow download for paid consumers or producers (not for unpaid consumers viewing watermarked docs)
  const canDownload = !isBlocked && (!showWatermark || isProducer);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh', maxHeight: '90vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" noWrap sx={{ maxWidth: '70%' }}>
          {doc?.title || t('documentViewer.title')}
        </Typography>
        <IconButton onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
        {isBlocked ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, height: 400 }}>
            <Typography variant="h6" color="text.secondary">
              {t('documentViewer.notAvailable')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('documentViewer.notAvailableHint')}
            </Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : images.length > 0 ? (
          <>
            {/* Toolbar */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {/* Page navigation */}
              {images.length > 1 && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconButton onClick={handlePrevPage} disabled={currentPage === 0} size="small">
                    <NavigateBeforeIcon />
                  </IconButton>
                  <Typography variant="body2">
                    {t('documentViewer.page')} {currentPage + 1} / {images.length}
                  </Typography>
                  <IconButton onClick={handleNextPage} disabled={currentPage === images.length - 1} size="small">
                    <NavigateNextIcon />
                  </IconButton>
                </Stack>
              )}

              {/* Zoom controls */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 200 }}>
                <IconButton onClick={handleZoomOut} disabled={zoom <= 50} size="small">
                  <ZoomOutIcon />
                </IconButton>
                <Slider
                  value={zoom}
                  onChange={handleZoomChange}
                  min={50}
                  max={200}
                  step={25}
                  sx={{ width: 100 }}
                  size="small"
                />
                <IconButton onClick={handleZoomIn} disabled={zoom >= 200} size="small">
                  <ZoomInIcon />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 45 }}>
                  {zoom}%
                </Typography>
              </Stack>
            </Box>

            {/* Document display */}
            <Box
              sx={{
                flexGrow: 1,
                overflow: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                p: 2,
                bgcolor: 'grey.100',
              }}
            >
              <Box
                component="img"
                src={images[currentPage]}
                alt={`${doc?.title || 'Document'} - Page ${currentPage + 1}`}
                sx={{
                  width: `${zoom}%`,
                  maxWidth: 'none',
                  height: 'auto',
                  boxShadow: 3,
                  borderRadius: 1,
                }}
              />
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
            <Typography color="text.secondary">
              {t('documentViewer.unsupportedFormat')}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('documentViewer.close')}</Button>
        {canDownload && !isBlocked && (
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            {t('documentViewer.download')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DocumentViewer;
